import { AppData, Registry } from '@lightningjs/sdk';
import { decodeBase64 } from 'support/generalUtils';
import axios, { AxiosError, AxiosResponse } from 'axios';
import { hoursToMilliseconds } from 'support/timeUtils';
import {
  ErrorCode,
  reportError,
} from 'services/analytics/reportingServicePage';

export type BlueconicStorage = {
  nexstarId: string;
  timestamp: number;
};

type BlueconicParams = {
  baseUrl: string;
};

const ID_TTL_MS = hoursToMilliseconds(24);
const RETRY_STATUS = [501, 503];

export default class BlueconicService {
  static readonly serviceName = 'BlueconicService';

  private baseUrl = '';
  private apiKey = '';

  private retryTimeoutId: number | null = null;
  private isRetrying = false;

  constructor(params: BlueconicParams) {
    const { baseUrl } = params;
    this.baseUrl = baseUrl;
  }

  private isEnabled() {
    return !!AppData?.privacy?.blueconic?.enabled;
  }

  async init() {
    if (!this.isEnabled()) return;

    const userOptOut = AppData?.privacyService.getDoNotSell();
    this.apiKey = decodeBase64(AppData?.privacy?.blueconic?.key ?? '');
    if (!this.apiKey || userOptOut) return;

    const hasActiveId = this.checkExistingId();
    if (!hasActiveId) {
      const isResolved = await this.requestId();
      if (!isResolved) await this.retryRequest();
    }
  }

  getId() {
    if (!this.isEnabled()) return '';

    const savedId = AppData?.storageService.blueconic.get();
    return savedId?.nexstarId ?? '';
  }

  private async getIfa() {
    const deviceInfo = await AppData!.device.getDeviceInfo();
    return deviceInfo.adId;
  }

  private checkExistingId() {
    const savedId = AppData?.storageService.blueconic.get();
    if (!savedId) return false;

    const isActive = Date.now() < savedId.timestamp + ID_TTL_MS;
    if (!isActive) AppData?.storageService.blueconic.remove();

    return isActive;
  }

  private async requestId() {
    const ctvId = await this.getIfa();
    const response = await this.getNexstarId(ctvId);
    return this.handleApiResponse(response);
  }

  private saveNewId(nexstarId: string) {
    AppData?.storageService.blueconic.set({
      nexstarId,
      timestamp: Date.now(),
    });
  }

  private async getNexstarId<TResponse = unknown>(ctvId: string) {
    try {
      const response = await axios.request<TResponse>({
        baseURL: this.baseUrl + '/' + ctvId,
        method: 'GET',
        headers: {
          Accept: 'application/json',
          'x-api-key': this.apiKey,
        },
      });

      return response;
    } catch (e) {
      return e as AxiosError;
    }
  }

  private async retryRequest() {
    if (this.isRetrying) return;

    const retryDelays = AppData?.ux?.blueconic_retry_time_delay_ms ?? [];
    if (!retryDelays.length) return;

    this.isRetrying = true;

    try {
      for (const delay of retryDelays) {
        await new Promise<void>(resolve => {
          this.retryTimeoutId = Registry.setTimeout(resolve, delay);
        });
        this.clearTimer();

        const isResolved = await this.requestId();
        if (isResolved) return;
      }

      // Retries exhausted, cache empty id
      this.saveNewId('');
    } finally {
      this.clearTimer();
      this.isRetrying = false;
    }
  }

  private clearTimer() {
    if (this.retryTimeoutId) {
      Registry.clearTimeout(this.retryTimeoutId);
      this.retryTimeoutId = null;
    }
  }

  // Returns false if request should be retried when failed
  private handleApiResponse(
    response: AxiosError<unknown, any> | AxiosResponse<unknown, any>,
  ): boolean {
    if (response.status === 200) {
      this.saveNewId((response as AxiosResponse).data.nexstar_id);
      return true;
    }

    reportError({
      error: response,
      userFacing: false,
      code: ErrorCode.c46,
    });

    // 501/503 - Retry. If all retries fail, save empty id to local cache so it doesn't retry until TTL expires
    if (RETRY_STATUS.includes(response.status as number)) return false;

    // 400/401/403/404 - Treat as no ID available, save empty id to local cache
    this.saveNewId('');
    return true;
  }
}
